import root from 'window-or-global';
import {useCallback, useContext, useEffect} from 'react';
import {
    buildURI,
    joiner,
    rowsToArrays,
    Data,
    Headers,
    getDefaultHeaders,
    Formatter
} from '../../../../../utils/CSVUtils';
import {CSVContent, CSVExportContext} from './BulkCSVExportProvider';
import {formatFileName} from '../../../../../utils/Utils';
import useTrackExportErrors from './useTrackExportErrors';

export type PrepareDataFunction = (data: Data) => [Data, Headers | undefined];

export interface UseCSVExportParams {
    data: Data;
    fileName: string;
    prepareData?: PrepareDataFunction;
    formatter?: Formatter;
}

export type UseCSVExport = () => void;

const getFileName = (name: string) => `${formatFileName(name)}.csv`;

const useCSVExport = ({
    data: paramsData,
    fileName,
    prepareData,
    formatter
}: UseCSVExportParams): UseCSVExport => {
    const {registerCSVContent, unregisterCSVContent} = useContext(CSVExportContext);
    const {trackCSVError} = useTrackExportErrors();

    const buildCSVContent = useCallback((): string => {
        const [data, headers] = prepareData
            ? prepareData(paramsData)
            : [paramsData, getDefaultHeaders(paramsData)];

        return joiner(rowsToArrays(data, headers, formatter));
    }, [paramsData]);

    const handleExport = useCallback(() => {
        try {
            const csv = buildCSVContent();

            const link = root.document.createElement('a');
            link.href = buildURI(csv);
            link.download = getFileName(fileName);
            link.click();
            link.remove();
        } catch (error) {
            trackCSVError(error);
        }
    }, [buildCSVContent, fileName]);

    useEffect(() => {
        const content: CSVContent = [getFileName(fileName), buildCSVContent];
        registerCSVContent(content);

        return () => {
            unregisterCSVContent(content);
        };
    });

    return handleExport;
};

export default useCSVExport;
